import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import FilterToolbar from '../../components/FilterToolbar';
import { getWorkOrders } from '../../api/workOrder';
import {
  fromApiWorkOrder, woIsBid, visibleWorkOrderListRows, woListKindLabel, woSourceBidNumber,
} from '../../utils/woEntity';
import { WO_STATUSES } from '../../constants/workOrder';
import { formatDate, formatCurrency } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS, canWorkOrder } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';

const PAGE_SIZE = 25;

export default function WoList() {
  const { can } = useInvRole();
  const { url } = useRouteMatch();
  const history = useHistory();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = {};
      if (statusFilter) p.status = statusFilter;
      if (search) p.search = search;
      const data = await getWorkOrders(p);
      const list = Array.isArray(data) ? data : (data?.items || data?.data || []);
      setRows(list.map(fromApiWorkOrder));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const newUrl = useMemo(() => url.replace('/work-orders', '/work-order/new'), [url]);
  const canCreate = canWorkOrder(can, INV_ACTIONS.WO_CREATE);
  const canEdit = canWorkOrder(can, INV_ACTIONS.WO_EDIT);
  const hasActiveFilters = Boolean(searchInput.trim() || statusFilter);
  const visibleRows = useMemo(
    () => visibleWorkOrderListRows(rows, { statusFilter, search }),
    [rows, statusFilter, search],
  );

  const columns = [
    { field: 'wo_number', headerName: 'WO #', width: 170,
      renderCell: (p) => {
        const source = woSourceBidNumber(p.row);
        return (
          <Box sx={{ lineHeight: 1.25, py: 0.25 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{p.row.wo_number}</Typography>
            {source ? (
              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                From bid {source}
              </Typography>
            ) : woIsBid(p.row) ? (
              <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                Collecting quotes
              </Typography>
            ) : null}
          </Box>
        );
      } },
    { field: 'wo_date', headerName: 'Date', width: 120,
      valueFormatter: (p) => formatDate(p.value) },
    { field: 'kind', headerName: 'Kind', width: 120,
      valueGetter: (p) => woListKindLabel(p.row) },
    { field: 'wo_type', headerName: 'Type', width: 140,
      valueGetter: (p) => p.row.wo_type || '—' },
    { field: 'title', headerName: 'Work', flex: 1, minWidth: 180 },
    { field: 'vendor_name', headerName: 'Vendor', width: 160,
      valueGetter: (p) => p.row.vendor_name || (woIsBid(p.row) ? 'Bidding' : '—') },
    { field: 'expected_completion', headerName: 'Expected', width: 120,
      valueFormatter: (p) => formatDate(p.value) },
    { field: 'grand_total', headerName: 'Amount', width: 120,
      valueFormatter: (p) => formatCurrency(p.value) },
    { field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => <StatusChip status={p.value} /> },
    { field: 'actions', headerName: '', width: 110, sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => history.push(`${url}/${p.row.id}`)}
              sx={{ color: IPD_COLORS.primary }}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {p.row.status === 'DRAFT' && canEdit && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => history.push(`${url}/${p.row.id}/edit`)}
                sx={{ color: IPD_COLORS.textSecondary }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ) },
  ];

  return (
    <Box>
      <PageHeader
        eyebrow="Material Management"
        title="Work Orders"
        description={`${visibleRows.length} record${visibleRows.length === 1 ? '' : 's'}`}
        actions={canCreate ? (
          <InvButton startIcon={<AddIcon />} onClick={() => history.push(newUrl)}>
            New Work Order
          </InvButton>
        ) : null}
      />

      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="WO number, title, vendor…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...WO_STATUSES.map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
          ]}
        />
        <FilterToolbar.Reset onClick={() => { setSearchInput(''); setStatusFilter(''); }} disabled={!hasActiveFilters} />
      </FilterToolbar>

      <InvDataGrid
        fillHeight
        rows={visibleRows}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        getRowId={(r) => r.id}
        getRowHeight={() => 58}
        emptyMessage="No work orders yet."
        pageSize={PAGE_SIZE}
        emptyAction={canCreate ? (
          <InvButton startIcon={<AddIcon />} onClick={() => history.push(newUrl)}>
            Create Work Order
          </InvButton>
        ) : null}
      />
    </Box>
  );
}
